import React, { useEffect, useState } from "react";
import NewHeader from "@/components/NewHeader";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle, RefreshCw, AlertCircle } from "lucide-react";
import { testMewsApiConnection, type TestResults } from "@/services/mews-api/test-connection";
import { fetchRoomCategoriesForMapping } from "@/services/mews-api/fetch-room-categories";

const TestMewsApi: React.FC = () => {
  const [results, setResults] = useState<TestResults | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [roomCategories, setRoomCategories] = useState<Array<{
    id: string;
    name: string;
    description?: string;
    ordering: number;
    normalBedCount: number;
    extraBedCount: number;
    spaceType: string;
    allNames: Record<string, string>;
  }> | null>(null);

  const runTests = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await testMewsApiConnection();
      setResults(res);
      
      // Also fetch room categories for manual mapping
      const roomCategoriesResult = await fetchRoomCategoriesForMapping();
      if (roomCategoriesResult.success) {
        setRoomCategories(roomCategoriesResult.roomCategories);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void runTests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Log all rate plan IDs and product/category IDs to console
  useEffect(() => {
    if (!results) return;

    console.log('='.repeat(80));
    console.log('📊 MEWS API - RATE PLAN IDs & PRODUCT/CATEGORY IDs');
    console.log('='.repeat(80));

    // Extract all rate plan IDs
    const ratePlanIds = new Set<string>();
    
    // From availability response - Rates array
    if (results.phase3Tests?.availability.data?.Rates) {
      results.phase3Tests.availability.data.Rates.forEach((rate) => {
        if (rate.Id) {
          ratePlanIds.add(rate.Id);
        }
      });
    }

    // From availability response - RoomCategoryAvailabilities -> Pricing -> RateId
    if (results.phase3Tests?.availability.data?.RoomCategoryAvailabilities) {
      results.phase3Tests.availability.data.RoomCategoryAvailabilities.forEach((roomAvail) => {
        if (roomAvail.RoomOccupancyAvailabilities) {
          roomAvail.RoomOccupancyAvailabilities.forEach((occAvail) => {
            if (occAvail.Pricing) {
              occAvail.Pricing.forEach((pricing) => {
                if (pricing.RateId) {
                  ratePlanIds.add(pricing.RateId);
                }
              });
            }
          });
        }
      });
    }

    // From reservations pricing response - RoomCategories -> RatePlans -> Id
    if (results.phase3Tests?.reservationsPricing.data?.RoomCategories) {
      results.phase3Tests.reservationsPricing.data.RoomCategories.forEach((roomCat) => {
        if (roomCat.RatePlans) {
          roomCat.RatePlans.forEach((ratePlan) => {
            if (ratePlan.Id) {
              ratePlanIds.add(ratePlan.Id);
            }
          });
        }
      });
    }

    // Log all rate plan IDs
    if (ratePlanIds.size > 0) {
      console.log('\n💰 ALL AVAILABLE RATE PLAN IDs:');
      console.log('─'.repeat(80));
      const sortedRateIds = Array.from(ratePlanIds).sort();
      sortedRateIds.forEach((rateId, index) => {
        // Try to find rate name from availability response
        let rateName = 'Unknown';
        if (results.phase3Tests?.availability.data?.Rates) {
          const rate = results.phase3Tests.availability.data.Rates.find((r) => r.Id === rateId);
          if (rate?.Name) {
            const nameObj = rate.Name as Record<string, string>;
            rateName = nameObj['en-US'] || nameObj['en'] || Object.values(nameObj)[0] || 'Unknown';
          }
        }
        console.log(`  ${index + 1}. ${rateId}`);
        console.log(`     Name: ${rateName}`);
      });
      console.log(`\n  Total: ${ratePlanIds.size} unique rate plan ID(s)`);
    } else {
      console.log('\n💰 RATE PLAN IDs: None found in current test data');
    }

    // Extract all product IDs and category IDs
    const productIds = new Set<string>();
    const categoryIds = new Set<string>();
    const productCategoryMap = new Map<string, string>(); // productId -> categoryId

    // From hotels response - Products array
    if (results.hotelsTest.data?.Products) {
      results.hotelsTest.data.Products.forEach((product) => {
        if (product.Id) {
          productIds.add(product.Id);
        }
        if (product.CategoryId) {
          categoryIds.add(product.CategoryId);
          productCategoryMap.set(product.Id, product.CategoryId);
        }
      });
    }

    // Log all product IDs
    if (productIds.size > 0) {
      console.log('\n🛍️  ALL AVAILABLE PRODUCT IDs:');
      console.log('─'.repeat(80));
      const sortedProductIds = Array.from(productIds).sort();
      sortedProductIds.forEach((productId, index) => {
        // Find product name from hotels response
        let productName = 'Unknown';
        let categoryId = productCategoryMap.get(productId) || 'No category';
        if (results.hotelsTest.data?.Products) {
          const product = results.hotelsTest.data.Products.find((p) => p.Id === productId);
          if (product?.Name) {
            const nameObj = product.Name as Record<string, string>;
            productName = nameObj['en-US'] || nameObj['en'] || Object.values(nameObj)[0] || 'Unknown';
          }
          if (product?.CategoryId) {
            categoryId = product.CategoryId;
          }
        }
        console.log(`  ${index + 1}. ${productId}`);
        console.log(`     Name: ${productName}`);
        console.log(`     Category ID: ${categoryId}`);
      });
      console.log(`\n  Total: ${productIds.size} unique product ID(s)`);
    } else {
      console.log('\n🛍️  PRODUCT IDs: None found in current test data');
    }

    // Log all unique category IDs
    if (categoryIds.size > 0) {
      console.log('\n📁 ALL AVAILABLE PRODUCT CATEGORY IDs:');
      console.log('─'.repeat(80));
      const sortedCategoryIds = Array.from(categoryIds).sort();
      sortedCategoryIds.forEach((categoryId, index) => {
        // Count products in this category
        const productsInCategory = Array.from(productIds).filter(
          (pid) => productCategoryMap.get(pid) === categoryId
        ).length;
        console.log(`  ${index + 1}. ${categoryId}`);
        console.log(`     Products in category: ${productsInCategory}`);
      });
      console.log(`\n  Total: ${categoryIds.size} unique category ID(s)`);
    } else {
      console.log('\n📁 PRODUCT CATEGORY IDs: None found in current test data');
    }

    console.log('\n' + '='.repeat(80));
    console.log('💡 Tip: Use these IDs to configure VITE_MEWS_RATE_MAPPING_JSON and VITE_MEWS_CATEGORY_MAPPING_JSON');
    console.log('='.repeat(80) + '\n');
  }, [results]);

  const allPassed =
    !!results &&
    results.configTest.success &&
    results.hotelsTest.success &&
    results.paymentConfigTest.success;

  const mappingTestsPassed =
    !!results?.mappingTests &&
    results.mappingTests.occupancyConversion.success &&
    results.mappingTests.roomMapping.success &&
    results.mappingTests.productMapping.success &&
    results.mappingTests.ratePlanMapping.success;

  const renderApiStatus = (label: string, success: boolean) => (
    <div className="flex flex-col items-center gap-1">
      <div className="text-2xl">{success ? "✅" : "❌"}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  );

  const getLocalizedName = (value: unknown): string => {
    if (!value) return "Unknown";
    if (typeof value === "string") return value;
    if (typeof value === "object") {
      const obj = value as Record<string, unknown>;
      const en = obj["en-US"];
      if (typeof en === "string") return en;
      const first = Object.values(obj)[0];
      if (typeof first === "string") return first;
    }
    return "Unknown";
  };

  return (
    <div className="min-h-screen flex flex-col">
      <NewHeader lightBackground />

      <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight mb-2">Mews API Connection Test</h1>
          <p className="text-muted-foreground">
            Internal diagnostics page to verify your Mews Booking Engine configuration, hotels data,
            payment configuration, and Phase 2 mapping utilities.
          </p>
        </div>

        <div className="mb-4 flex gap-2">
          <Button
            variant="outline"
            onClick={() => void runTests()}
            disabled={loading}
            className="inline-flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Running Tests...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Run Tests Again
              </>
            )}
          </Button>
        </div>

        {error && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <div className="font-semibold text-red-700">Error</div>
                  <div className="text-sm text-red-700">{error}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {loading && !results && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <div className="text-sm text-muted-foreground">Testing API connection...</div>
              </div>
            </CardContent>
          </Card>
        )}

        {results && (
          <div className="space-y-4">
            <Card className={allPassed ? "border-green-200 bg-green-50" : "border-yellow-200 bg-yellow-50"}>
              <CardHeader>
                <div className="flex items-center justify-between gap-2">
                  <CardTitle>API Connection Test Summary</CardTitle>
                  <Badge variant={allPassed ? "default" : "destructive"}>
                    {allPassed ? "All API Tests Passed" : "Some API Tests Failed"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  {renderApiStatus("Configuration", results.configTest.success)}
                  {renderApiStatus("Hotels", results.hotelsTest.success)}
                  {renderApiStatus("Payment Config", results.paymentConfigTest.success)}
                </div>
              </CardContent>
            </Card>

            {/* Configuration Test */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {results.configTest.success ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                  <span>Configuration Test</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {results.configTest.success ? (
                  <>
                    <div className="text-sm text-green-700 font-medium">✅ Test passed</div>
                    {results.configTest.data && (
                      <div className="mt-4 space-y-2 text-sm">
                        <div>
                          <span className="font-medium">Age Categories:</span>{" "}
                          {results.configTest.data.AgeCategories?.length ?? 0}
                        </div>
                        <div>
                          <span className="font-medium">Policies:</span>{" "}
                          {results.configTest.data.Policies?.length ?? 0}
                        </div>
                        {results.configTest.data.Currency && (
                          <div>
                            <span className="font-medium">Currency:</span>{" "}
                            {results.configTest.data.Currency}
                          </div>
                        )}
                        {results.configTest.data.CheckInTime && (
                          <div>
                            <span className="font-medium">Check-in Time:</span>{" "}
                            {results.configTest.data.CheckInTime}
                          </div>
                        )}
                        {results.configTest.data.CheckOutTime && (
                          <div>
                            <span className="font-medium">Check-out Time:</span>{" "}
                            {results.configTest.data.CheckOutTime}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="text-sm text-red-700 font-medium">❌ Test failed</div>
                    {results.configTest.error && (
                      <div className="mt-2 text-sm text-red-700">{results.configTest.error}</div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Hotels Test */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {results.hotelsTest.success ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                  <span>Hotels Test</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {results.hotelsTest.success && results.hotelsTest.data ? (
                  <>
                    <div className="text-sm text-green-700 font-medium">✅ Test passed</div>
                    <div className="mt-4 space-y-2 text-sm">
                      <div>
                        <span className="font-medium">Hotel:</span>{" "}
                        {getLocalizedName(results.hotelsTest.data.Name)}
                      </div>
                      <div>
                        <span className="font-medium">Room Categories:</span>{" "}
                        {results.hotelsTest.data.RoomCategories?.length ?? 0}
                      </div>
                      {results.hotelsTest.data.RoomCategories &&
                        results.hotelsTest.data.RoomCategories.length > 0 && (
                          <div className="mt-2">
                            <div className="font-medium mb-1 text-sm">Sample Room Categories:</div>
                            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                              {results.hotelsTest.data.RoomCategories.slice(0, 5).map((room) => {
                                const name = getLocalizedName(room.Name);
                                const id =
                                  typeof room.Id === "string" && room.Id.length > 8
                                    ? `${room.Id.slice(0, 8)}...`
                                    : String(room.Id ?? "");
                                return (
                                  <li key={room.Id}>
                                    {name} <span className="text-xs text-muted-foreground">(ID: {id})</span>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                      {results.hotelsTest.data.Products &&
                        results.hotelsTest.data.Products.length > 0 && (
                          <div>
                            <span className="font-medium">Products:</span>{" "}
                            {results.hotelsTest.data.Products.length}
                          </div>
                        )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-sm text-red-700 font-medium">❌ Test failed</div>
                    {results.hotelsTest.error && (
                      <div className="mt-2 text-sm text-red-700">{results.hotelsTest.error}</div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Payment Configuration Test */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {results.paymentConfigTest.success ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                  <span>Payment Configuration Test</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {results.paymentConfigTest.success && results.paymentConfigTest.data ? (
                  <>
                    <div className="text-sm text-green-700 font-medium">✅ Test passed</div>
                    <div className="mt-4 space-y-2 text-sm">
                      {results.paymentConfigTest.data.PaymentGateway && (
                        <>
                          <div>
                            <span className="font-medium">Payment Gateway:</span>{" "}
                            {results.paymentConfigTest.data.PaymentGateway.PaymentGatewayType}
                          </div>
                          <div>
                            <span className="font-medium">Is Merchant:</span>{" "}
                            {results.paymentConfigTest.data.PaymentGateway.IsMerchant ? "Yes" : "No"}
                          </div>
                          {results.paymentConfigTest.data.PaymentGateway.SupportedCreditCardTypes &&
                            results.paymentConfigTest.data.PaymentGateway.SupportedCreditCardTypes.length >
                              0 && (
                              <div>
                                <span className="font-medium">Supported Cards:</span>{" "}
                                {results.paymentConfigTest.data.PaymentGateway.SupportedCreditCardTypes.join(
                                  ", ",
                                )}
                              </div>
                            )}
                          {results.paymentConfigTest.data.PaymentGateway.DefaultCurrencyCode && (
                            <div>
                              <span className="font-medium">Default Currency:</span>{" "}
                              {results.paymentConfigTest.data.PaymentGateway.DefaultCurrencyCode}
                            </div>
                          )}
                        </>
                      )}

                      {results.paymentConfigTest.data.SurchargeConfiguration && (
                        <>
                          <div>
                            <span className="font-medium">Surcharge Service ID:</span>{" "}
                            <span className="text-xs font-mono">
                              {results.paymentConfigTest.data.SurchargeConfiguration.SurchargeServiceId
                                ? String(
                                    results.paymentConfigTest.data.SurchargeConfiguration
                                      .SurchargeServiceId,
                                  ).slice(0, 8) + "..."
                                : "None"}
                            </span>
                          </div>
                          {results.paymentConfigTest.data.SurchargeConfiguration.SurchargeFees && (
                            <div className="mt-2">
                              <div className="font-medium text-sm">Surcharge Fees:</div>
                              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                                {Object.entries(
                                  results.paymentConfigTest.data.SurchargeConfiguration.SurchargeFees,
                                ).map(([card, fee]) => (
                                  <li key={card}>
                                    {card}:{" "}
                                    {`${(Number(fee as number) * 100).toFixed(2).toString()}%`}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-sm text-red-700 font-medium">❌ Test failed</div>
                    {results.paymentConfigTest.error && (
                      <div className="mt-2 text-sm text-red-700">
                        {results.paymentConfigTest.error}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Mapping Tests */}
            {results.mappingTests && allPassed && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-2xl font-bold tracking-tight">Mapping Functions Tests</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Tests for Phase 2: Data Mapping &amp; Translation (occupancy, rooms, products, and
                    rate plans).
                  </p>
                </div>

                {/* Summary */}
                <Card
                  className={
                    mappingTestsPassed
                      ? "border-green-200 bg-green-50"
                      : "border-yellow-200 bg-yellow-50"
                  }
                >
                  <CardHeader>
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle>Mapping Tests Summary</CardTitle>
                      <Badge variant={mappingTestsPassed ? "default" : "destructive"}>
                        {mappingTestsPassed
                          ? "All Mapping Tests Passed"
                          : "Some Mapping Tests Had Issues"}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div className="flex flex-col items-center gap-1">
                        <div className="text-xl">
                          {results.mappingTests.occupancyConversion.success ? "✅" : "⚠️"}
                        </div>
                        <div className="text-muted-foreground text-xs text-center">
                          Occupancy Conversion
                        </div>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <div className="text-xl">
                          {results.mappingTests.roomMapping.success ? "✅" : "⚠️"}
                        </div>
                        <div className="text-muted-foreground text-xs text-center">Room Mapping</div>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <div className="text-xl">
                          {results.mappingTests.productMapping.success ? "✅" : "⚠️"}
                        </div>
                        <div className="text-muted-foreground text-xs text-center">
                          Product Mapping
                        </div>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <div className="text-xl">
                          {results.mappingTests.ratePlanMapping.success ? "✅" : "⚠️"}
                        </div>
                        <div className="text-muted-foreground text-xs text-center">
                          Rate Plan Mapping
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Occupancy Conversion Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {results.mappingTests.occupancyConversion.success ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-yellow-600" />
                      )}
                      <span>Occupancy Conversion</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {results.mappingTests.occupancyConversion.testCases &&
                    results.mappingTests.occupancyConversion.testCases.length > 0 ? (
                      <div className="space-y-2 text-sm">
                        <p className="text-muted-foreground">
                          Converts adults/children counts into Mews <code>OccupancyData</code> lines.
                        </p>
                        <div className="mt-2 border rounded-md overflow-hidden">
                          <div className="grid grid-cols-5 bg-muted px-3 py-2 text-xs font-medium text-muted-foreground">
                            <span>Adults</span>
                            <span>Children</span>
                            <span>Result entries</span>
                            <span>Has adult line</span>
                            <span>Has child line</span>
                          </div>
                          {results.mappingTests.occupancyConversion.testCases.map((t, idx) => (
                            <div
                              key={`${t.adults}-${t.children}-${idx}`}
                              className="grid grid-cols-5 px-3 py-1.5 text-xs border-t"
                            >
                              <span>{t.adults}</span>
                              <span>{t.children}</span>
                              <span>{t.resultCount}</span>
                              <span>{t.hasAdult ? "✅" : "❌"}</span>
                              <span>{t.hasChild ? "✅" : t.children === 0 ? "—" : "❌"}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground">
                        No occupancy test cases returned from the API test helper.
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Room Mapping Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {results.mappingTests.roomMapping.success ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-yellow-600" />
                      )}
                      <span>Room Mapping</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 text-sm">
                      <div className="space-y-1">
                        <div>
                          <span className="font-medium">Total local rooms:</span>{" "}
                          {results.mappingTests.roomMapping.totalRooms}
                        </div>
                        <div>
                          <span className="font-medium">Matched rooms:</span>{" "}
                          {results.mappingTests.roomMapping.matchedRooms}{" "}
                          <span className="text-xs text-muted-foreground">
                            ({results.mappingTests.roomMapping.matchRate.toFixed(1)}%)
                          </span>
                        </div>
                      </div>

                      {results.mappingTests.roomMapping.matchedDetails &&
                        results.mappingTests.roomMapping.matchedDetails.length > 0 && (
                          <div>
                            <div className="font-medium mb-1">Matched details (sample):</div>
                            <ul className="list-disc list-inside text-xs text-muted-foreground space-y-1">
                              {results.mappingTests.roomMapping.matchedDetails
                                .slice(0, 8)
                                .map((m) => (
                                  <li key={`${m.localId}-${m.mewsId}`}>
                                    <span className="font-mono">{m.localId}</span> →{" "}
                                    {m.mewsName}{" "}
                                    <span className="text-[10px]">
                                      ({String(m.mewsId).slice(0, 8)}...)
                                    </span>
                                  </li>
                                ))}
                              {results.mappingTests.roomMapping.matchedDetails.length > 8 && (
                                <li className="text-[10px] italic">
                                  +{" "}
                                  {results.mappingTests.roomMapping.matchedDetails.length - 8} more
                                </li>
                              )}
                            </ul>
                          </div>
                        )}

                      {results.mappingTests.roomMapping.unmatchedRooms &&
                        results.mappingTests.roomMapping.unmatchedRooms.length > 0 && (
                          <div>
                            <div className="font-medium mb-1">Unmatched local rooms:</div>
                            <p className="text-xs text-muted-foreground">
                              {results.mappingTests.roomMapping.unmatchedRooms.join(", ")}
                            </p>
                          </div>
                        )}
                    </div>
                  </CardContent>
                </Card>

                {/* Product Mapping Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {results.mappingTests.productMapping.success ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-yellow-600" />
                      )}
                      <span>Product Mapping</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 text-sm">
                      <div className="space-y-1">
                        <div>
                          <span className="font-medium">Total products:</span>{" "}
                          {results.mappingTests.productMapping.totalProducts}
                        </div>
                        <div>
                          <span className="font-medium">Mapped products:</span>{" "}
                          {results.mappingTests.productMapping.mappedProducts}
                        </div>
                        <div>
                          <span className="font-medium">Skipped products:</span>{" "}
                          {results.mappingTests.productMapping.skippedProducts}
                        </div>
                      </div>

                      {results.mappingTests.productMapping.mappedDetails &&
                        results.mappingTests.productMapping.mappedDetails.length > 0 && (
                          <div>
                            <div className="font-medium mb-1">Mapped products (sample):</div>
                            <ul className="list-disc list-inside text-xs text-muted-foreground space-y-1">
                              {results.mappingTests.productMapping.mappedDetails
                                .slice(0, 8)
                                .map((p) => (
                                  <li key={p.id}>
                                    {p.name}{" "}
                                    <span className="text-[10px]">
                                      ({p.category}
                                      {p.price !== null ? ` • CHF ${p.price}` : " • no price"})
                                    </span>
                                  </li>
                                ))}
                              {results.mappingTests.productMapping.mappedDetails.length > 8 && (
                                <li className="text-[10px] italic">
                                  +{" "}
                                  {results.mappingTests.productMapping.mappedDetails.length - 8} more
                                </li>
                              )}
                            </ul>
                          </div>
                        )}
                    </div>
                  </CardContent>
                </Card>

                {/* Rate Plan Mapping Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {results.mappingTests.ratePlanMapping.success ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-yellow-600" />
                      )}
                      <span>Rate Plan Mapping</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {results.mappingTests.ratePlanMapping.testCases &&
                    results.mappingTests.ratePlanMapping.testCases.length > 0 ? (
                      <div className="space-y-3 text-sm">
                        <p className="text-muted-foreground">
                          Shows how Mews rate names/IDs are mapped to local rate plan IDs. Rates with manual mappings (configured in VITE_MEWS_RATE_MAPPING_JSON) are highlighted.
                        </p>
                        
                        {/* Statistics */}
                        {(() => {
                          const totalRates = results.mappingTests.ratePlanMapping.testCases?.length || 0;
                          const mappedRates = results.mappingTests.ratePlanMapping.testCases?.filter(t => t.isMapped).length || 0;
                          const unmappedRates = totalRates - mappedRates;
                          return (
                            <div className="flex gap-4 text-xs">
                              <div>
                                <span className="font-medium">Total rates:</span>{" "}
                                <span className="text-muted-foreground">{totalRates}</span>
                              </div>
                              <div>
                                <span className="font-medium text-green-700">✅ Mapped:</span>{" "}
                                <span className="text-muted-foreground">{mappedRates}</span>
                              </div>
                              <div>
                                <span className="font-medium text-yellow-700">⚠️  Unmapped:</span>{" "}
                                <span className="text-muted-foreground">{unmappedRates}</span>
                              </div>
                            </div>
                          );
                        })()}

                        <div className="mt-2 border rounded-md overflow-hidden">
                          <div className="grid grid-cols-4 bg-muted px-3 py-2 text-xs font-medium text-muted-foreground">
                            <span>Rate name</span>
                            <span>Rate ID</span>
                            <span>Mapped to</span>
                            <span>Status</span>
                          </div>
                          {results.mappingTests.ratePlanMapping.testCases.map((t, idx) => {
                            const isMapped = t.isMapped ?? false;
                            return (
                              <div
                                key={`${t.rateId}-${idx}`}
                                className={`grid grid-cols-4 px-3 py-1.5 text-xs border-t ${
                                  isMapped 
                                    ? "bg-green-50/50 hover:bg-green-50" 
                                    : "hover:bg-muted/30"
                                }`}
                              >
                                <span className={isMapped ? "font-medium" : ""}>{t.rateName}</span>
                                <span className="font-mono text-[10px] break-all">
                                  {t.rateId}
                                </span>
                                <span className={isMapped ? "font-medium text-green-700" : ""}>
                                  {t.mappedTo ?? "standard (default)"}
                                </span>
                                <span>
                                  {isMapped ? (
                                    <Badge variant="default" className="bg-green-600 text-white text-[10px] px-1.5 py-0">
                                      ✅ Mapped
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                      ⚠️  Auto
                                    </Badge>
                                  )}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground">
                        {results.phase3Tests?.availability.success 
                          ? "Waiting for rate plan data from availability API..."
                          : "No rate plan mapping test cases returned. Phase 3 API tests may not have completed yet."}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* All Available Mews Room Categories for Manual Mapping */}
            {roomCategories && roomCategories.length > 0 && (
              <div className="mt-8 space-y-4">
                <div>
                  <h2 className="text-2xl font-bold tracking-tight">Available Mews Room Categories</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Use this list to manually map local room IDs to Mews RoomCategoryId values.
                  </p>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>All Room Categories ({roomCategories.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="text-sm text-muted-foreground mb-4">
                        <p className="mb-2">
                          <strong>Local room IDs to map:</strong>
                        </p>
                        <ul className="list-disc list-inside space-y-1 font-mono text-xs">
                          <li>junior-suite</li>
                          <li>family-suite</li>
                          <li>superior-double-room</li>
                          <li>double-twin-balcony-lake</li>
                          <li>double-twin-balcony-village</li>
                          <li>double-twin-noBal</li>
                          <li>standard-triple-room</li>
                          <li>single-balcony-lake</li>
                          <li>heritage-single</li>
                          <li>basic-single</li>
                        </ul>
                      </div>

                      <div className="border rounded-md overflow-hidden">
                        <div className="bg-muted px-4 py-2 text-xs font-medium text-muted-foreground grid grid-cols-12 gap-2">
                          <span className="col-span-1">#</span>
                          <span className="col-span-3">Name</span>
                          <span className="col-span-5">ID</span>
                          <span className="col-span-1">Beds</span>
                          <span className="col-span-1">Extra</span>
                          <span className="col-span-1">Type</span>
                        </div>
                        {roomCategories.map((room, idx) => (
                          <div
                            key={room.id}
                            className="grid grid-cols-12 gap-2 px-4 py-2 text-xs border-t hover:bg-muted/50"
                          >
                            <span className="col-span-1 text-muted-foreground">{idx + 1}</span>
                            <span className="col-span-3 font-medium">{room.name}</span>
                            <span className="col-span-5 font-mono text-[10px] break-all">
                              {room.id}
                            </span>
                            <span className="col-span-1 text-muted-foreground">{room.normalBedCount}</span>
                            <span className="col-span-1 text-muted-foreground">{room.extraBedCount}</span>
                            <span className="col-span-1 text-muted-foreground">{room.spaceType}</span>
                          </div>
                        ))}
                      </div>

                      {roomCategories.some(r => r.description) && (
                        <div className="mt-4 p-3 bg-muted rounded-md text-xs">
                          <strong>Note:</strong> Some rooms may have descriptions. Check the API response for full details.
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Phase 3: Availability & Pricing Tests */}
            {results.phase3Tests && allPassed && (
              <div className="mt-8 space-y-4">
                <div>
                  <h2 className="text-2xl font-bold tracking-tight">Phase 3: Availability & Pricing APIs</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Tests for Phase 3: Availability & Pricing Integration (room availability, pricing, and service/add-on APIs).
                  </p>
                </div>

                {/* Summary */}
                <Card
                  className={
                    results.phase3Tests.availability.success &&
                    results.phase3Tests.reservationsPricing.success
                      ? "border-green-200 bg-green-50"
                      : "border-yellow-200 bg-yellow-50"
                  }
                >
                  <CardHeader>
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle>Phase 3 Tests Summary</CardTitle>
                      <Badge
                        variant={
                          results.phase3Tests.availability.success &&
                          results.phase3Tests.reservationsPricing.success
                            ? "default"
                            : "destructive"
                        }
                      >
                        {results.phase3Tests.availability.success &&
                        results.phase3Tests.reservationsPricing.success
                          ? "Phase 3 APIs Working"
                          : "Some Phase 3 Tests Had Issues"}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div className="flex flex-col items-center gap-1">
                        <div className="text-xl">
                          {results.phase3Tests.availability.success ? "✅" : "❌"}
                        </div>
                        <div className="text-muted-foreground text-xs text-center">
                          Availability
                        </div>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <div className="text-xl">
                          {results.phase3Tests.reservationsPricing.success ? "✅" : "❌"}
                        </div>
                        <div className="text-muted-foreground text-xs text-center">
                          Reservations Pricing
                        </div>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <div className="text-xl">
                          {results.phase3Tests.servicesAvailability.success ? "✅" : "⚠️"}
                        </div>
                        <div className="text-muted-foreground text-xs text-center">
                          Services Availability
                        </div>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <div className="text-xl">
                          {results.phase3Tests.servicesPricing.success ? "✅" : "⚠️"}
                        </div>
                        <div className="text-muted-foreground text-xs text-center">
                          Services Pricing
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Availability Test */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {results.phase3Tests.availability.success ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-600" />
                      )}
                      <span>getAvailability()</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {results.phase3Tests.availability.success &&
                    results.phase3Tests.availability.data ? (
                      <div className="space-y-2 text-sm">
                        <div className="text-green-700 font-medium">✅ Test passed</div>
                        <div className="mt-2 space-y-1">
                          <div>
                            <span className="font-medium">Available room categories:</span>{" "}
                            {results.phase3Tests.availability.data.RoomCategoryAvailabilities?.length ?? 
                             results.phase3Tests.availability.data.RoomCategories?.length ?? 0}
                          </div>
                          {results.phase3Tests.availability.requestParams && (
                            <div className="mt-2 text-xs text-muted-foreground">
                              <div>
                                <span className="font-medium">Test dates:</span>{" "}
                                {new Date(
                                  results.phase3Tests.availability.requestParams.startUtc
                                ).toLocaleDateString()}{" "}
                                -{" "}
                                {new Date(
                                  results.phase3Tests.availability.requestParams.endUtc
                                ).toLocaleDateString()}
                              </div>
                              <div>
                                <span className="font-medium">Occupancy:</span>{" "}
                                {results.phase3Tests.availability.requestParams.occupancyData
                                  .map(
                                    (occ) =>
                                      `${occ.PersonCount}x ${occ.AgeCategoryId.substring(0, 8)}...`
                                  )
                                  .join(", ")}
                              </div>
                            </div>
                          )}
                          {(results.phase3Tests.availability.data.RoomCategoryAvailabilities &&
                            results.phase3Tests.availability.data.RoomCategoryAvailabilities.length > 0) ||
                          (results.phase3Tests.availability.data.RoomCategories &&
                            results.phase3Tests.availability.data.RoomCategories.length > 0) ? (
                              <div className="mt-2">
                                <div className="font-medium mb-1 text-xs">Sample available rooms:</div>
                                <ul className="list-disc list-inside text-xs text-muted-foreground space-y-1">
                                  {results.phase3Tests.availability.data.RoomCategoryAvailabilities &&
                                  results.phase3Tests.availability.data.RoomCategoryAvailabilities.length > 0
                                    ? results.phase3Tests.availability.data.RoomCategoryAvailabilities
                                        .slice(0, 5)
                                        .map((room) => (
                                          <li key={room.RoomCategoryId}>
                                            {room.RoomCategoryId.substring(0, 8)}... - Available:{" "}
                                            {room.AvailableRoomCount}
                                          </li>
                                        ))
                                    : results.phase3Tests.availability.data.RoomCategories
                                        ?.slice(0, 5)
                                        .map((room) => (
                                          <li key={room.RoomCategoryId}>
                                            {room.RoomCategoryId.substring(0, 8)}... - Available:{" "}
                                            {room.AvailableCount} ({room.IsAvailable ? "Yes" : "No"})
                                          </li>
                                        ))}
                                </ul>
                              </div>
                            ) : null}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm">
                        <div className="text-red-700 font-medium">❌ Test failed</div>
                        {results.phase3Tests.availability.error && (
                          <div className="mt-2 text-sm text-red-700">
                            {results.phase3Tests.availability.error}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Reservations Pricing Test */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {results.phase3Tests.reservationsPricing.success ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-600" />
                      )}
                      <span>getReservationsPricing()</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {results.phase3Tests.reservationsPricing.success &&
                    results.phase3Tests.reservationsPricing.data ? (
                      <div className="space-y-2 text-sm">
                        <div className="text-green-700 font-medium">✅ Test passed</div>
                        <div className="mt-2 space-y-1">
                          {results.phase3Tests.reservationsPricing.data.TotalPrice && (
                            <div>
                              <span className="font-medium">Total price:</span>{" "}
                              {results.phase3Tests.reservationsPricing.data.Currency || "CHF"}{" "}
                              {results.phase3Tests.reservationsPricing.data.TotalPrice}
                            </div>
                          )}
                          {results.phase3Tests.reservationsPricing.data.RoomCategories &&
                            results.phase3Tests.reservationsPricing.data.RoomCategories.length >
                              0 && (
                              <div>
                                <span className="font-medium">Room categories with pricing:</span>{" "}
                                {
                                  results.phase3Tests.reservationsPricing.data.RoomCategories
                                    .length
                                }
                              </div>
                            )}
                          {results.phase3Tests.reservationsPricing.requestParams && (
                            <div className="mt-2 text-xs text-muted-foreground">
                              <div>
                                <span className="font-medium">Room category ID:</span>{" "}
                                {results.phase3Tests.reservationsPricing.requestParams.roomCategoryId.substring(
                                  0,
                                  8
                                )}
                                ...
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm">
                        <div className="text-red-700 font-medium">❌ Test failed</div>
                        {results.phase3Tests.reservationsPricing.error && (
                          <div className="mt-2 text-sm text-red-700">
                            {results.phase3Tests.reservationsPricing.error}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Services Availability Test */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {results.phase3Tests.servicesAvailability.success ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-yellow-600" />
                      )}
                      <span>getServicesAvailability()</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {results.phase3Tests.servicesAvailability.success &&
                    results.phase3Tests.servicesAvailability.data ? (
                      <div className="space-y-2 text-sm">
                        <div className="text-green-700 font-medium">✅ Test passed</div>
                        <div className="mt-2 space-y-1">
                          {results.phase3Tests.servicesAvailability.data.Availability && (
                            <div>
                              <span className="font-medium">Availability entries:</span>{" "}
                              {results.phase3Tests.servicesAvailability.data.Availability.length}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm">
                        <div className="text-yellow-700 font-medium">
                          {results.phase3Tests.servicesAvailability.error?.includes("Skipped")
                            ? "⏭️  Skipped"
                            : "❌ Test failed"}
                        </div>
                        {results.phase3Tests.servicesAvailability.error && (
                          <div className="mt-2 text-sm text-yellow-700">
                            {results.phase3Tests.servicesAvailability.error}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Services Pricing Test */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {results.phase3Tests.servicesPricing.success ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-yellow-600" />
                      )}
                      <span>getServicesPricing()</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {results.phase3Tests.servicesPricing.success &&
                    results.phase3Tests.servicesPricing.data ? (
                      <div className="space-y-2 text-sm">
                        <div className="text-green-700 font-medium">✅ Test passed</div>
                        <div className="mt-2 space-y-1">
                          {results.phase3Tests.servicesPricing.data.Pricing && (
                            <div>
                              <span className="font-medium">Pricing entries:</span>{" "}
                              {results.phase3Tests.servicesPricing.data.Pricing.length}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm">
                        <div className="text-yellow-700 font-medium">
                          {results.phase3Tests.servicesPricing.error?.includes("Skipped")
                            ? "⏭️  Skipped"
                            : "❌ Test failed"}
                        </div>
                        {results.phase3Tests.servicesPricing.error && (
                          <div className="mt-2 text-sm text-yellow-700">
                            {results.phase3Tests.servicesPricing.error}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* All Available Mews Products (Services/Add-ons) */}
            {results?.hotelsTest.success &&
              results.hotelsTest.data?.Products &&
              results.hotelsTest.data.Products.length > 0 && (
                <div className="mt-8 space-y-4">
                  <div>
                    <h2 className="text-2xl font-bold tracking-tight">Available Mews Products</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Raw list of products/services returned by the Hotels API. These are the source items
                      for upsell mapping.
                    </p>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle>
                        All Products ({results.hotelsTest.data.Products.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="border rounded-md overflow-hidden">
                          <div className="bg-muted px-4 py-2 text-xs font-medium text-muted-foreground grid grid-cols-12 gap-2">
                            <span className="col-span-1">#</span>
                            <span className="col-span-3">Name</span>
                            <span className="col-span-4">ID</span>
                            <span className="col-span-1">Included</span>
                            <span className="col-span-1">Pricing</span>
                            <span className="col-span-2">Category ID</span>
                          </div>
                          {results.hotelsTest.data.Products.map((product, idx) => (
                            <div
                              key={product.Id}
                              className="grid grid-cols-12 gap-2 px-4 py-2 text-xs border-t hover:bg-muted/50"
                            >
                              <span className="col-span-1 text-muted-foreground">{idx + 1}</span>
                              <span className="col-span-3 font-medium">
                                {getLocalizedName(product.Name)}
                              </span>
                              <span className="col-span-4 font-mono text-[10px] break-all">
                                {product.Id}
                              </span>
                              <span className="col-span-1 text-muted-foreground">
                                {product.IncludedByDefault ? "Yes" : "No"}
                              </span>
                              <span className="col-span-1 text-muted-foreground">
                                {product.Pricing?.Discriminator ?? "Unknown"}
                              </span>
                              <span className="col-span-2 font-mono text-[10px] break-all text-muted-foreground">
                                {product.CategoryId}
                              </span>
                            </div>
                          ))}
                        </div>

                        <div className="mt-2 text-xs text-muted-foreground">
                          <p>
                            <strong>Note:</strong> Products with{" "}
                            <code>IncludedByDefault = true</code> are not treated as upsells. Pricing with
                            <code>Discriminator = &quot;Relative&quot;</code> cannot be converted to an
                            absolute upsell price without additional context.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default TestMewsApi;

